// The admin on Firebase (SPEC-33, SPEC-34, SPEC-44). The modules load on first use,
// as for the sign-in screens (src/lib/firebaseModules.ts). The claim is
// read from the session's token as it stands, with no round trip (SPEC-43);
// a token without it is refreshed on the spot and read again, so a team
// access granted minutes ago counts without signing out. The callables
// check the claim again and answer permission-denied or unauthenticated
// without it, so a token that still says team after the access was
// withdrawn reaches the refusal at the first call; adminAccount answers
// not-found for an account that does not exist.
//
// A log line carries a code, never an email or a row.

import { firebaseSignIn } from "../account/firebaseSignIn";
import { codeOf } from "../account/firebaseError";
import { FUNCTIONS_REGION } from "../data/transport";
import { loadFirebase } from "../lib/firebaseModules";
import type {
  AdminBilanRequest,
  AdminBilanResponse,
  AdminCreateAccountRequest,
  AdminCreateAccountResponse,
  AdminExportResponse,
  AdminLanguageRequest,
  AdminLanguageResponse,
  AdminProfileRequest,
  AdminProfileResponse,
  AdminUsersResponse,
} from "../schema/types";
import type { AdminSource } from "./adminSource";
import { AdminDeniedError, AdminEmailTakenError, AdminNotFoundError } from "./adminSource";

/** The answers that mean the caller is no team account. */
const DENIED = new Set(["functions/permission-denied", "functions/unauthenticated"]);

/** The answer that means no account has that id. */
const NOT_FOUND = "functions/not-found";

/** The answer that means an account already has that email (adminCreateAccount). */
const TAKEN = "functions/already-exists";

/** The base and its export take no request data. */
type NoRequest = Record<string, never>;

async function call<Request, Response>(name: string, request: Request): Promise<Response> {
  const [{ app }, , functions] = await loadFirebase();
  try {
    const callable = functions.httpsCallable<Request, Response>(functions.getFunctions(app, FUNCTIONS_REGION), name);
    return (await callable(request)).data;
  } catch (error) {
    const code = codeOf(error);
    if (DENIED.has(code)) throw new AdminDeniedError(name);
    if (code === NOT_FOUND) throw new AdminNotFoundError(name);
    if (code === TAKEN) throw new AdminEmailTakenError(name);
    throw error;
  }
}

export const firebaseAdmin: AdminSource = {
  async access() {
    const [{ auth }, authApi] = await loadFirebase();
    await auth.authStateReady();
    const user = auth.currentUser;
    if (!user) return "signed-out";
    const isTeam = async (refresh: boolean) => (await authApi.getIdTokenResult(user, refresh)).claims["team"] === true;
    const team = (await isTeam(false)) || (await isTeam(true));
    return team ? "team" : "denied";
  },

  signIn: (email, password) => firebaseSignIn.withPassword(email, password),

  async signOut() {
    const [{ auth }, authApi] = await loadFirebase();
    await authApi.signOut(auth);
  },

  users: () => call<NoRequest, AdminUsersResponse>("adminUsers", {}),

  exportCsv: () => call<NoRequest, AdminExportResponse>("adminExport", {}),

  profile: (accountId, purpose) => call<AdminProfileRequest, AdminProfileResponse>("adminAccount", { accountId, purpose }),

  bilan: (request) => call<AdminBilanRequest, AdminBilanResponse>("adminBilan", request),

  setLanguage: (accountId, language) => call<AdminLanguageRequest, AdminLanguageResponse>("adminLanguage", { accountId, language }),

  /**
   * SPEC-54: the server creates the account; then Firebase's own reset email,
   * in her language, is her invitation to set a password (HANDOFF F7 points
   * its link at the app). The team member's session is untouched: the email
   * is sent for an address, not signed in as it.
   */
  async createAccount(request) {
    const { accountId } = await call<AdminCreateAccountRequest, AdminCreateAccountResponse>("adminCreateAccount", request);
    const [{ auth }, authApi] = await loadFirebase();
    const languageBefore = auth.languageCode;
    try {
      auth.languageCode = request.language;
      await authApi.sendPasswordResetEmail(auth, request.email.trim().toLowerCase());
      return { accountId, invited: true };
    } catch (error) {
      console.error("[admin] invitation not sent", { code: codeOf(error) });
      return { accountId, invited: false };
    } finally {
      auth.languageCode = languageBefore;
    }
  },
};
